import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    latitude: Number,
    longitude: Number,
    zoom: { type: Number, default: 16 },
    minZoom: { type: Number, default: 8 },
    city: String,
    region: String,
    country: String,
    pharmacyLocations: Array,
    searchId: Number
  }

  static targets = ["map", "notification"]

  connect() {
    this.mapInstance = null
    this.initAttempts = 0
    this.maxAttempts = 50

    this.waitForLeaflet()
  }

  disconnect() {
    this.cleanup()
  }

  waitForLeaflet() {
    if (typeof L !== 'undefined' && typeof L.map === 'function') {
      this.initMap()
    } else {
      this.initAttempts++
      if (this.initAttempts < this.maxAttempts) {
        setTimeout(() => this.waitForLeaflet(), 100)
      } else {
        console.error('Leaflet failed to load')
        if (this.hasMapTarget) {
          this.mapTarget.innerHTML = '<div style="padding: 20px; text-align: center; color: #6b7280;">Map failed to load. Please refresh.</div>'
        }
      }
    }
  }

  cleanup() {
    if (this.mapInstance) {
      try { this.mapInstance.remove() } catch(e) {}
      this.mapInstance = null
    }

    if (this.hasMapTarget && this.mapTarget._leaflet_id) {
      this.mapTarget.innerHTML = ''
      delete this.mapTarget._leaflet_id
    }
  }

  initMap() {
    this.cleanup()

    if (!this.hasMapTarget) return

    setTimeout(() => {
      if (this.mapTarget._leaflet_id) return

      const lat = this.latitudeValue
      const lng = this.longitudeValue
      if (!lat || !lng) return

      try {
        this.mapInstance = L.map(this.mapTarget, { center: [lat, lng], zoom: this.zoomValue })

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: 'OpenStreetMap contributors',
          maxZoom: 19
        }).addTo(this.mapInstance)

        this.addUserMarker(lat, lng)
        this.addPharmacyMarkers()

        setTimeout(() => {
          if (this.mapInstance) {
            this.mapInstance.invalidateSize()
            this.adjustZoomToShowResults()
          }
        }, 100)
      } catch(error) {
        console.error('Error initializing map:', error)
      }
    }, 200)
  }

  addUserMarker(lat, lng) {
    const locationText = [this.cityValue, this.regionValue, this.countryValue].filter(x => x).join(', ')

    const userIcon = L.divIcon({
      className: 'custom-marker',
      html: '<div style="background-color: #3b82f6; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
      iconSize: [20, 20],
      iconAnchor: [10, 10]
    })

    L.marker([lat, lng], { icon: userIcon })
      .addTo(this.mapInstance)
      .bindPopup(`<div style="padding: 8px;"><h3 style="font-weight: 600; margin-bottom: 4px;">Your Location</h3><p style="color: #4b5563;">${locationText}</p></div>`)
  }

  addPharmacyMarkers() {
    const pharmacyIcon = L.divIcon({
      className: 'custom-marker',
      html: '<div style="background-color: #ef4444; width: 16px; height: 16px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
      iconSize: [16, 16],
      iconAnchor: [8, 8]
    })

    const locations = this.pharmacyLocationsValue || []

    locations.forEach(loc => {
      if (!loc.latitude || !loc.longitude) return

      const name = loc.pharmacy ? loc.pharmacy.name : 'Pharmacy'
      const addr = [loc.address_1, loc.city, loc.state].filter(x => x).join(', ')

      let pricesHtml = ''
      if (loc.prices && loc.prices.length > 0) {
        pricesHtml = `<div class="pharmacy-prices" style="margin-top: 10px; border-top: 1px solid #e5e7eb; padding-top: 10px; max-height: 200px; overflow-y: auto;">`

        loc.prices.forEach(p => {
          const linkStyle = 'color: #3b82f6; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1;'
          const linkContent = p.url
            ? `<a href="${p.url}" target="_blank" title="${p.medicine_name}" style="${linkStyle}">${p.medicine_name}</a>`
            : `<span title="${p.medicine_name}" style="${linkStyle}">${p.medicine_name}</span>`

          pricesHtml += `<div style="font-size: 12px; display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
            ${linkContent}
            <span style="color: #059669; font-weight: 600; white-space: nowrap;">$${parseFloat(p.price).toFixed(2)}</span>
          </div>`
        })
        pricesHtml += '</div>'
      }

      const popupContent = `<div class="pharmacy-popup" style="padding: 12px;">
        <h3 style="font-weight: 600; margin-bottom: 4px;">${name}</h3>
        ${addr ? `<p style="font-size: 13px; color: #4b5563;">${addr}</p>` : ''}
        ${pricesHtml}
      </div>`

      const marker = L.marker([parseFloat(loc.latitude), parseFloat(loc.longitude)], { icon: pharmacyIcon })
        .addTo(this.mapInstance)
        .bindPopup(popupContent, { minWidth: 350 })

      marker.on('click', () => this.trackMarkerClick(loc.id))
    })
  }

  trackMarkerClick(pharmacyLocationId) {
    if (!this.hasSearchIdValue || !pharmacyLocationId) return

    const payload = JSON.stringify({
      search_id: this.searchIdValue,
      event_type: "map_marker",
      pharmacy_location_id: pharmacyLocationId
    })

    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" })
      navigator.sendBeacon("/clicks", blob)
      return
    }

    fetch("/clicks", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
      credentials: "same-origin"
    }).catch(() => {})
  }

  // Auto-zoom helper methods

  pharmaciesWithPrices() {
    const locations = this.pharmacyLocationsValue || []
    return locations.filter(loc =>
      loc.prices && loc.prices.length > 0 && loc.latitude && loc.longitude
    )
  }

  calculateDistanceKm(lat1, lng1, lat2, lng2) {
    const R = 6371 // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180
    const dLng = (lng2 - lng1) * Math.PI / 180
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
    return R * c
  }

  findNearestPharmacyWithPrices() {
    const pharmaciesWithPrices = this.pharmaciesWithPrices()
    if (pharmaciesWithPrices.length === 0) return null

    const userLat = this.latitudeValue
    const userLng = this.longitudeValue
    let nearest = null
    let minDistance = Infinity

    pharmaciesWithPrices.forEach(loc => {
      const dist = this.calculateDistanceKm(
        userLat, userLng,
        parseFloat(loc.latitude), parseFloat(loc.longitude)
      )
      if (dist < minDistance) {
        minDistance = dist
        nearest = { ...loc, distanceKm: dist }
      }
    })

    return nearest
  }

  hasVisiblePharmacyWithPrices() {
    if (!this.mapInstance) return false
    const bounds = this.mapInstance.getBounds()
    return this.pharmaciesWithPrices().some(loc => {
      const latLng = L.latLng(parseFloat(loc.latitude), parseFloat(loc.longitude))
      return bounds.contains(latLng)
    })
  }

  adjustZoomToShowResults() {
    const pharmaciesWithPrices = this.pharmaciesWithPrices()

    // If no pharmacies have results anywhere, keep default view
    if (pharmaciesWithPrices.length === 0) {
      return
    }

    // Check if any pharmacy with prices is already visible
    if (this.hasVisiblePharmacyWithPrices()) {
      return
    }

    // Find nearest pharmacy with prices
    const nearest = this.findNearestPharmacyWithPrices()
    if (!nearest) return

    // Calculate bounds to include user location and nearest pharmacy
    const userLatLng = L.latLng(this.latitudeValue, this.longitudeValue)
    const pharmacyLatLng = L.latLng(
      parseFloat(nearest.latitude),
      parseFloat(nearest.longitude)
    )

    const bounds = L.latLngBounds([userLatLng, pharmacyLatLng])

    // Fit bounds with padding, respecting min/max zoom
    this.mapInstance.fitBounds(bounds, {
      padding: [50, 50],
      maxZoom: this.zoomValue,
      minZoom: this.minZoomValue
    })

    // Show notification with distance
    this.showZoomNotification(nearest.distanceKm)
  }

  showZoomNotification(distanceKm) {
    if (!this.hasNotificationTarget) return

    const distanceText = distanceKm < 1
      ? `${Math.round(distanceKm * 1000)}m`
      : `${Math.round(distanceKm)}km`

    this.notificationTarget.textContent = `Farmacia más cercana a ${distanceText}`
    this.notificationTarget.classList.remove('hidden')
  }
}
